// 1. Framework / library imports
import { memo, useCallback, useEffect, useState } from "react";

// 2. Helpers & Hooks
import { classNames, useTranslate } from "helpers";

// 3. API & Types
import {
  VehicleType,
  VehicleTypeEditFormValues,
} from "services/api/catalog/vehicles/types/types";
import { initialVehicleTypeEditForm } from "services/api/catalog/vehicles/types/consts";
import { getVehicleTypeAPI } from "services/api/catalog/vehicles/types/api";
import { getVehiclesByCvhJomAPI } from "services/api/catalog/vehicles/api";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { CvhVehicleSearchRow } from "services/api/catalog/cvh/types";
import { initialDataWithPagination } from "services/api/consts";

// 4. UI Components
import VehicleCategoryPicker from "ui/components/VehicleCategoryPicker/VehicleCategoryPicker";
import VehicleManufacturerDropdown from "ui/components/Dropdown/VehicleManufacturerDropdown/VehicleManufacturerDropdown";
import VehicleModelDropdown from "ui/components/Dropdown/VehicleModelDropdown/VehicleModelDropdown";
import VehicleTypeDropdown from "ui/components/Dropdown/VehicleTypeDropdown/VehicleTypeDropdown";
import JOMDropdown from "ui/components/Dropdown/JOMDropdown/JOMDropdown";

// 5. Local types
import { VehiclePickerFormProps } from "./types";
import { initialVehicleInfo } from "../../consts";
import { Button } from "ui/components/Button/Button";
import { CatalogVehicleResult } from "services/api/catalog/vehicles/types";
import Tabs from "ui/components/Tabs/Tabs";

const VehiclePickerForm: React.FC<VehiclePickerFormProps> = (props) => {
  const {
    formValues,
    setFormValues,
    errors,
    setErrors,
    vehicleInfo,
    setVehicleInfo,
    onJomSelect,
    onTabChange,
    showHeader,
    title = "Kreiranje novog vozila",
    description = "Izaberite vozilo koje želite kreirati klikom na izbornik ispod. Nakon toga možete unijeti sve potrebne podatke klikom na gumb naprijed u doljnjem desnom kutu.",
  } = props;

  const { t } = useTranslate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [vehicleImage, setVehicleImage] = useState<string>("");
  const [vehicleTypeFormValue, setVehicleTypeFormValue] =
    useState<VehicleTypeEditFormValues>(initialVehicleTypeEditForm);

  const [openModel, setOpenModel] = useState(false);
  const [openType, setOpenType] = useState(false);
  const [jomValue, setJomValue] = useState<string>("");
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(() => {
    const saved = localStorage.getItem("vehiclePickerTab");
    return saved ? Number(saved) : 0;
  });

  const handleTabChange = useCallback((index: number) => {
    setActiveTabIndex(index);
    localStorage.setItem("vehiclePickerTab", String(index));
    onTabChange?.(index);
  }, [onTabChange]);

  // Zapamćena kartica se ne bira klikom, pa je roditelj inače ne bi ni doznao.
  useEffect(() => {
    onTabChange?.(activeTabIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track selected names for VehicleInfo
  const [selectedManufacturerName, setSelectedManufacturerName] = useState<string>("");
  const [selectedModelName, setSelectedModelName] = useState<string>("");

  useEffect(() => {
    if (formValues.vehicleTypeId) {
      fetchVehicleType(
        formValues.vehicleManufacturerId ?? "",
        formValues.vehicleModelId ?? "",
        formValues.vehicleTypeId ?? "",
        selectedManufacturerName,
        selectedModelName,
        vehicleImage
      );
    }
  }, [formValues.vehicleTypeId]);

  const fetchVehicleType = useCallback(
    async (
      vehicleManufacturerId: string,
      vehicleModelId: string,
      vehicleTypeId: string,
      manufacturerName?: string,
      modelName?: string,
      modelImage?: string
    ) => {
      if (
        vehicleManufacturerId === "" ||
        vehicleModelId === "" ||
        vehicleTypeId === ""
      ) {
        setVehicleTypeFormValue(initialVehicleTypeEditForm);
        setVehicleInfo(initialVehicleInfo);
        return;
      }

      setIsLoading(true);
      setVehicleInfo((prev) => ({ ...prev, isLoading: true }));

      let result: VehicleType;
      try {
        result = await getVehicleTypeAPI(
          vehicleManufacturerId,
          vehicleModelId,
          vehicleTypeId
        );
        if (result) {
          setFormValues((prev) => ({
            ...prev,
            vehicleTypeId: result.id,
          }));
          setVehicleTypeFormValue({
            ...result,
            transmission: result.transmission?.id ?? null,
            drivenWheels: result.drivenWheels?.id ?? null,
            fuelType: result.fuelType
              ? { id: result.fuelType.id, name: result.fuelType.name ?? "" }
              : null,
          });
          setVehicleInfo((prev) => ({
            // Imena proizvođača/modela nisu izvedena iz tipa — kad picker nema svoja (npr. na uređivanju),
            // sačuvaj već postavljena (VehicleEditCard ih puni iz učitanog vozila).
            isLoading: false,
            vehicleManufacturerName: manufacturerName || selectedManufacturerName || prev.vehicleManufacturerName || null,
            vehicleModelName: modelName || selectedModelName || prev.vehicleModelName || null,
            vehicleTypeName: result.name || null,
            vehicleImage: result.image || modelImage || vehicleImage || null,
            fuelType: result.fuelType?.name || null,
            transmission: result.transmission?.name || null,
            maximumPowerHp: result.maximumPowerHp || null,
            maximumPowerKw: result.maximumPowerKw || null,
            cylinderCapacity: result.cylinderCapacity || null,
            numberOfDoors: result.numberOfDoors || null,
            seats: result.seats || null,
            jom: result.jom || null,
            equipmentCount: null,
            tireDimensions: null,
            tireManufacturer: null,
            tireType: null,
            tireCount: null,
            dot: null,
            profileDepth: null,
            rof: null,
            fireExtinguisherName: null,
            fireExtinguisherExpirationDate: null,
            fireExtinguisherPrice: null,
            fireExtinguisherWeight: null,
            fireExtinguisherSerialNumber: null,
            ownerTypeId: null,
            ownerTypeName: null,
            ownerContactName: null,
            ownerPurchaseDate: null,
            ownerLeaseActivationDate: null,
            netPrice: null,
            specialTax: null,
            sellingPrice: null,
            netDiscount: null,
            vehicleInVatSystem: null,
            pictureUrls: [],
            documentTypesStatus: [],
            vinNumber: null,
            kmTraveled: null,
            registrationPlate: null,
          }));
        }
      } catch {
        setIsLoading(false);
        setVehicleInfo((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      setIsLoading(false);
      setVehicleInfo((prev) => ({ ...prev, isLoading: false }));
    },
    [setFormValues, setVehicleTypeFormValue, setVehicleInfo]
  );

  const handleVehicleCategoryChange = useCallback((e: any, name: string, item: DropdownOption) => {
    setIsCategoryLoading(true);
    setFormValues((prev) => ({
      ...prev,
      vehicleCategoryId: item.value,
      vehicleManufacturerId: "",
      vehicleModelId: "",
      vehicleTypeId: "",
    }));
    setSelectedManufacturerName("");
    setSelectedModelName("");
    setVehicleImage("");
    localStorage.setItem("lastVehicleCategoryId", item.value);
    setTimeout(() => setIsCategoryLoading(false), 300);
  }, [setFormValues]);

  const handleVehicleManufacturerChange = useCallback((e: any, name: string, item: DropdownOption) => {
    setFormValues((prev) => ({
      ...prev,
      vehicleManufacturerId: item.value,
      vehicleModelId: "",
      vehicleTypeId: "",
    }));
    setErrors((prev) => {
      const { vehicleManufacturerId, ...rest } = prev;
      return rest;
    });
    setSelectedManufacturerName(item.text || "");
    setSelectedModelName("");
    setVehicleImage("");
    setOpenModel(true);
  }, [setErrors]);

  const handleVehicleModelChange = useCallback((e: any, name: string, item: DropdownOption) => {
    setFormValues((prev) => ({
      ...prev,
      vehicleModelId: item.value,
      vehicleTypeId: "",
    }));
    setErrors((prev) => {
      const { vehicleModelId, ...rest } = prev;
      return rest;
    });
    // `text` modela je JSX (naziv + godine), a naziv se sprema kao tekst — zato `displayName`.
    setSelectedModelName(item.displayName || (typeof item.text === "string" ? item.text : ""));
    if (item.image) setVehicleImage(item.image);
    setOpenType(true);
  }, [setErrors]);

  const handleVehicleTypeChange = useCallback((e: any, name: string, item: DropdownOption) => {
    setFormValues((prev) => ({
      ...prev,
      vehicleTypeId: item.value,
    }));
    setErrors((prev) => {
      const { vehicleTypeId, ...rest } = prev;
      return rest;
    });
  }, [setErrors]);

  const handleJomSelect = useCallback((vehicle: CatalogVehicleResult) => {
    setFormValues((prev) => ({
      ...prev,
      vehicleManufacturerId: vehicle.vehicleManufacturer?.id || "",
      vehicleModelId: vehicle.vehicleModel?.id || "",
      vehicleTypeId: vehicle.vehicleType?.id || "",
    }));
    // Odabir vozila preko JOM-a poništava validacijske greške koraka.
    setErrors({});
    setSelectedManufacturerName(vehicle.vehicleManufacturer?.name || "");
    setSelectedModelName(vehicle.vehicleModel?.name || "");
    setVehicleInfo({
      vehicleManufacturerName: vehicle.vehicleManufacturer?.name || null,
      vehicleModelName: vehicle.vehicleModel?.name || null,
      vehicleTypeName: vehicle.vehicleType?.name || null,
      vehicleImage: vehicle.vehicleType?.image || null,
      fuelType: vehicle.vehicleType?.fuelType?.name || null,
      transmission: vehicle.vehicleType?.transmission?.name || null,
      maximumPowerHp: vehicle.vehicleType?.maximumPowerHp || null,
      maximumPowerKw: vehicle.vehicleType?.maximumPowerKw || null,
      cylinderCapacity: vehicle.vehicleType?.cylinderCapacity || null,
      numberOfDoors: vehicle.vehicleType?.numberOfDoors || null,
      seats: vehicle.vehicleType?.seats || null,
      jom: vehicle.vehicleType?.jom || null,
      equipmentCount: null,
      tireDimensions: null,
      tireManufacturer: null,
      tireType: null,
      tireCount: null,
      dot: null,
      profileDepth: null,
      rof: null,
      fireExtinguisherName: null,
      fireExtinguisherExpirationDate: null,
      fireExtinguisherPrice: null,
      fireExtinguisherWeight: null,
      fireExtinguisherSerialNumber: null,
      ownerTypeId: null,
      ownerTypeName: null,
      ownerContactName: null,
      ownerPurchaseDate: null,
      ownerLeaseActivationDate: null,
      netPrice: null,
      specialTax: null,
      sellingPrice: null,
      netDiscount: null,
      vehicleInVatSystem: null,
      pictureUrls: [],
      documentTypesStatus: [],
      vinNumber: null,
      kmTraveled: null,
      registrationPlate: null,
    });
    onJomSelect?.(vehicle);
  }, [setVehicleInfo, setFormValues, onJomSelect]);

  const handleJomDropdownChange = useCallback((_e: any, _name: string, item: DropdownOption) => {
    setJomValue(item?.value ?? "");
  }, []);

  const handleJomDropdownSelect = useCallback(async (row: CvhVehicleSearchRow | null) => {
    if (!row) return;

    setIsLoading(true);
    setVehicleInfo((prev) => ({ ...prev, isLoading: true }));
    try {
      const results = await getVehiclesByCvhJomAPI(row.jom, initialDataWithPagination.pagination);
      const vehicle =
        results.data.find((v) => v.vehicleType?.jom === row.jom) || results.data[0];
      if (vehicle) handleJomSelect(vehicle);
    } catch {
      // greška se prikazuje kroz API interceptor
    } finally {
      setIsLoading(false);
      setVehicleInfo((prev) => ({ ...prev, isLoading: false }));
    }
  }, [handleJomSelect, setVehicleInfo]);


  return (
    <div className="flex h-full w-full">
      <form noValidate className="w-full ">
        <div className="flex flex-col">
          <div className="flex-1">
            {showHeader && (
              <>
                <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">{title}</h1>
                <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">{description}</p>
              </>
            )}

            <Tabs activeIndex={activeTabIndex} onTabChange={handleTabChange} tabs={[
              {
                label: t("admin.fleet.vehicle.picker.tab.search"),
                content: (
                  <div className="flex flex-col gap-4 p-4 border border-slate-200 mt-[-1px] w-full">
                    <VehicleCategoryPicker
                      name="vehicleCategoryId"
                      value={formValues.vehicleCategoryId}
                      onChange={handleVehicleCategoryChange}
                    />
                    <div className={classNames("flex flex-col gap-2 relative transition-all", (!formValues.vehicleCategoryId || isCategoryLoading) && "opacity-50 blur-[1px] pointer-events-none")}>
                      <div>
                        <label className="block text-sm font-medium leading-6 text-[#344054]">
                          {t("admin.fleet.vehicle.picker.label")}
                        </label>
                        <p className="text-xs text-slate-400 mt-0.5">{t("admin.fleet.vehicle.picker.description")}</p>
                      </div>
                    <div className="flex flex-col gap-3">
                      {isCategoryLoading && (
                        <div className="absolute inset-0 flex items-center justify-center z-10">
                          <svg aria-hidden="true" className="w-6 h-6 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                          </svg>
                        </div>
                      )}
                      <div className="w-full">
                        <VehicleManufacturerDropdown
                          placeholder={t("admin.vehicle.manufacturer.placeholder")}
                          name="vehicleManufacturerId"
                          value={formValues.vehicleManufacturerId}
                          vehicleCategoryId={formValues.vehicleCategoryId}
                          onChange={handleVehicleManufacturerChange}
                          error={errors.vehicleManufacturerId}
                          required={true}
                        />
                      </div>
                      <div className="w-full">
                        <VehicleModelDropdown
                          vehicleManufacturerId={formValues.vehicleManufacturerId}
                          placeholder={t("admin.vehicle.model.placeholder")}
                          name="vehicleModelId"
                          value={formValues.vehicleModelId}
                          onChange={handleVehicleModelChange}
                          error={formValues.vehicleManufacturerId ? errors.vehicleModelId : undefined}
                          disabled={!formValues.vehicleManufacturerId}
                          required={true}
                          autoOpen={openModel}
                        />
                      </div>
                      <div className="w-full">
                        <VehicleTypeDropdown
                          vehicleManufacturerId={formValues.vehicleManufacturerId}
                          vehicleModelId={formValues.vehicleModelId}
                          placeholder={t("admin.vehicle.type.placeholder")}
                          name="vehicleTypeId"
                          value={formValues.vehicleTypeId}
                          onChange={handleVehicleTypeChange}
                          error={formValues.vehicleModelId ? errors.vehicleTypeId : undefined}
                          disabled={!formValues.vehicleModelId}
                          required={true}
                          autoOpen={openType}
                        />
                      </div>
                    </div>
                    </div>
                  </div>
                ),
              },
              {
                label: t("admin.fleet.vehicle.picker.tab.jom"),
                content: (
                  <div className="p-4 border border-slate-200 mt-[-1px]">
                    <div className="mb-4">
                      <p className="text-md font-bold text-slate-800 mb-2">
                        {t("admin.fleet.vehicle.picker.jom.title")}
                      </p>
                      <p className="text-sm text-slate-600" dangerouslySetInnerHTML={{ __html: t("admin.fleet.vehicle.picker.jom.description") }}>
                      </p>
                    </div>

                    <JOMDropdown
                      name="jom"
                      label={t("admin.fleet.vehicle.picker.tab.jom")}
                      value={jomValue}
                      onChange={handleJomDropdownChange}
                      onSelect={handleJomDropdownSelect}
                      placeholder={t("admin.fleet.vehicle.picker.jom.button")}
                      width="full"
                      error={errors.vehicleTypeId ? "Molimo Vas da odaberete vozilo putem JOM broja" : undefined}
                    />

                    {vehicleInfo.jom && (
                      <div className="mt-4 flex items-center gap-4 p-3 border border-slate-200 rounded bg-slate-50">
                        <div className="flex-shrink-0 w-[80px] h-[60px] bg-white rounded overflow-hidden flex items-center justify-center">
                          {vehicleInfo.vehicleImage ? (
                            <img src={vehicleInfo.vehicleImage} alt="" className="w-full h-full object-contain" />
                          ) : (
                            <img src="/media/images/vehicles/vehicle-1.png" alt="" className="w-full h-full object-contain" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">
                            {vehicleInfo.vehicleManufacturerName} {vehicleInfo.vehicleModelName}
                          </p>
                          <p className="text-xs text-slate-500 truncate">{vehicleInfo.vehicleTypeName}</p>
                          <p className="text-xs font-medium text-blue-600 mt-0.5">JOM: {vehicleInfo.jom}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ),
              },
            ]} />

          </div>
        </div>
      </form>
    </div>
  );
};
export default memo(VehiclePickerForm);
